import { useQuery } from '@tanstack/react-query';
import { adminService } from '@services/adminService';
import { TrendingUp, Users, CreditCard, BarChart3 } from 'lucide-react';

const StatCard = ({ label, value, sub }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
    <p className="text-gray-400 text-sm">{label}</p>
    <p className="text-white text-2xl font-bold mt-1">{value}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
);

const AdminMetrics = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => adminService.getMetrics(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const { acquisition = {}, usage = {}, growthByMonth = {} } = data || {};

  const monthEntries = Object.entries(growthByMonth)
    .filter(([k]) => k !== 'unknown')
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12);

  const maxCount = Math.max(...monthEntries.map(([, v]) => v), 1);

  return (
    <div>
      <h1 className="text-white text-2xl font-bold mb-1">Métricas</h1>
      <p className="text-gray-400 text-sm mb-6">Crescimento e uso da plataforma</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Novos (7 dias)"
          value={acquisition.newLast7Days ?? '—'}
          sub="tenants"
        />
        <StatCard
          label="Novos (30 dias)"
          value={acquisition.newLast30Days ?? '—'}
          sub="tenants"
        />
        <StatCard
          label="Taxa de conversão"
          value={`${acquisition.conversionRate ?? 0}%`}
          sub="free → pago"
        />
        <StatCard
          label="Usuários por tenant"
          value={usage.usersPerTenant ?? '—'}
          sub="média"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Growth chart */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-400" />
            Novos tenants por mês
          </h2>
          {monthEntries.length === 0 ? (
            <p className="text-gray-500 text-sm">Sem dados disponíveis</p>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {monthEntries.map(([month, count]) => (
                <div key={month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-white text-xs font-medium">{count}</span>
                  <div
                    className="w-full bg-indigo-600 rounded-t"
                    style={{ height: `${Math.round((count / maxCount) * 120)}px`, minHeight: 4 }}
                  />
                  <span className="text-gray-600 text-xs">
                    {month.substring(5)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage stats */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-400" />
            Uso total
          </h2>
          <div className="space-y-4">
            {[
              { label: 'Usuários cadastrados', value: usage.users ?? 0, icon: Users },
              { label: 'Contas abertas', value: usage.accounts ?? 0, icon: CreditCard },
              {
                label: 'Transações este mês',
                value: (usage.transactionsThisMonth ?? 0).toLocaleString('pt-BR'),
                icon: TrendingUp,
              },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Icon className="h-4 w-4 text-indigo-400" />
                  {label}
                </div>
                <span className="text-white font-semibold">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;
